import './App.scss';
import {HashRouter, Route, Routes} from 'react-router-dom';
import Login from "./content/Login";
import React, {useContext, useEffect, useState} from "react";
import NotFound from "./components/HttpError";
import AuthContext from "./components/Auth/AuthContext";
import NavHeader from "./components/NavHeader";
import SecRoute from "./components/SecRoute";
import Dashboard from "./content/Dashboard";
import UserProfile from "./content/UserProfile";
import {Customers, EditCustomer, Recordings} from "./content/Customers";
import {EditUser, UserDevices, Users, WorkingHours} from "./content/Users";
import Events from "./content/Events";
import SystemStatus from "./content/Mails";
import Settings from "./content/Settings";
import Alarm from "./content/Alarm";
import PubSub from "pubsub-js";
import {useRecoilValue} from "recoil";
import {
  grpcCustomerServiceSelector,
  grpcNotificationServiceSelector,
  grpcPrefServiceSelector,
  grpcUpdatesServiceSelector
} from "./state/grpc";
import {Devices, EditDevice} from "./content/Devices";
import {Theme} from "@carbon/react";
import Reports from "./content/Reports";
import {Calls, EditCall} from "./content/Calls";
import Footer from "./components/Footer";

function App() {
  const authCtx = useContext(AuthContext);
  const updatesService = useRecoilValue(grpcUpdatesServiceSelector)
  const notificationService = useRecoilValue(grpcNotificationServiceSelector)
  const systemService = useRecoilValue(grpcCustomerServiceSelector)
  const prefService = useRecoilValue(grpcPrefServiceSelector)
  const [cloudStatus, setCloudStatus] = useState(false)
  const [userNotifications, setUserNotifications] = useState(0)
  const [callsEnabled, setCallsEnabled] = useState(false)
  const [recordingRunning, setRecordingRunning] = useState(null)

  const broadcastNotifyAlarm = (eventType, alarm) => {
    PubSub.publish('ALARM_EVENT', {
      alarm: alarm,
      eventType: eventType
    })
  }

  const broadcastNotifyUser = (eventType, notification) => {
    PubSub.publish('USER_EVENT', notification)
    setUserNotifications(userNotifications + 1)
  }

  const establishConn = () => {
    const req = new proto.api.UpdatesSubscriptionRequest()
    const updatesStream = updatesService.subscribe(req, {})
    console.log("connecting to updates stream..")
    updatesStream.on("data", resp => {
      switch (resp.getUpdatetype()) {
        case proto.api.SubscriptionUpdateType.GRD_PING: {
          console.debug("upstream ping received")
          setCloudStatus(true)
          return
        }
        case proto.api.SubscriptionUpdateType.GRD_ALARM_OPENED:
        case proto.api.SubscriptionUpdateType.GRD_ALARM_CLOSED:
        case proto.api.SubscriptionUpdateType.GRD_ALARM_UPDATED: {
          broadcastNotifyAlarm(resp.getUpdatetype(), resp.getAlarm().toObject())
          break
        }
        case proto.api.SubscriptionUpdateType.GRD_USER_NOTIFICATION: {
          broadcastNotifyUser(resp.getUpdatetype(), resp.getNotification().toObject())
          break
        }
      }
    })
    updatesStream.on("status", function (status) {
      console.log(status.code, status.details, status.metadata);
    });
    updatesStream.on("end", () => {
      console.log("updates stream closed");
      setCloudStatus(false)
    });
    updatesStream.on("error", (err) => {
      console.error(err)
      setCloudStatus(false)
    })
  }

  const updateNotificationsStatus = () => {
    const req = new proto.api.ListNotificationRequest()
    const pageReq = new proto.api.PageRequest()
    pageReq.setSort("datetime desc")
    pageReq.setSize(50)
    pageReq.setPage(1)
    req.setPagination(pageReq)
    notificationService.listNotifications(req, null, (err, resp) => {
      if (err != null) {
        console.error(err)
      } else {
        setUserNotifications(resp.toObject().notificationsList.filter(it => !it.read).length)
      }
    })
  }

  const updateCallsStatus = () => {
    const req = new proto.api.GetPrefRequest()
    req.setName("calls_enabled")
    prefService.getPref(req, null, (err, resp) => {
      if (err != null) {
        console.error(err)
      } else {
        setCallsEnabled(resp.toObject().value === "true")
      }
    })
  }

  const togglesCallsStatus = (enabled) => {
    setCallsEnabled(enabled)
    const req = new proto.api.UpdatePrefRequest()
    req.setName("calls_enabled")
    req.setValue(enabled ? "true" : "false")
    prefService.updatePref(req, null, (err) => {
      if (err != null) {
        console.error(err)
        setCallsEnabled(!enabled)
      }
    })
  }

  const updateRecordingStatus = () => {
    const req = new proto.api.GetUserRunningRecordingRequest()
    systemService.getUserRunningRecording(req, null, (err, resp) => {
      if (err == null) {
        const rec = resp.toObject().recording
        setRecordingRunning(rec)
      }
    })
  }

  const recoverConn = () => {
    if (!cloudStatus) {
      console.log("recovering upstream connection..")
      establishConn()
    }
  }

  useEffect(() => {
    if (!authCtx.isLoggedIn) {
      return
    }
    establishConn()
    updateNotificationsStatus()
    updateCallsStatus()
    updateRecordingStatus()
  }, [authCtx.isLoggedIn])

  return (
    <div className="App">
      <HashRouter>
        {authCtx.isLoggedIn &&
          <Theme theme="g10">
            <NavHeader
              upConnRecoverer={recoverConn}
              upstreamConnected={cloudStatus}
              userNotifications={userNotifications}
              callsEnabled={callsEnabled}
              runningRec={recordingRunning}
              setRecordingRunning={setRecordingRunning}
            />
          </Theme>
        }
        <Routes>
          <Route path="/" index element={
            <SecRoute>
              <Dashboard
                cloudConnected={cloudStatus}
                updateRunningRecording={(rec) => setRecordingRunning(rec)}
                runningRecording={recordingRunning}/>
            </SecRoute>
          }/>
          <Route path="/dashboard" index element={
            <SecRoute>
              <Dashboard
                cloudConnected={cloudStatus}
                updateRunningRecording={(rec) => setRecordingRunning(rec)}
                runningRecording={recordingRunning}/>
            </SecRoute>
          }/>
          <Route path="/profile" index element={<SecRoute><UserProfile/></SecRoute>}/>
          <Route path="/customers" element={
            <SecRoute>
              <Customers
                updateRunningRecording={(rec) => setRecordingRunning(rec)}
                runningRecording={recordingRunning}/>
            </SecRoute>
          }/>
          <Route path="/customers/:customerCode" element={<SecRoute><EditCustomer/></SecRoute>}/>
          <Route path="/customers/add" element={<SecRoute><EditCustomer/></SecRoute>}/>
          <Route path="/customers/:systemCode/devices" element={<SecRoute><Devices/></SecRoute>}/>
          <Route path="/customers/:systemCode/devices/add" element={<SecRoute><EditDevice/></SecRoute>}/>
          <Route path="/customers/:systemCode/devices/:deviceID" element={<SecRoute><EditDevice/></SecRoute>}/>
          <Route path="/recordings" element={<SecRoute><Recordings/></SecRoute>}/>
          <Route path="/users">
            <Route path="list" index element={<SecRoute><Users/></SecRoute>}/>
            <Route path="add" element={<SecRoute><EditUser/></SecRoute>}/>
            <Route path="devices" element={<SecRoute><UserDevices/></SecRoute>}/>
            <Route path="shifts" element={<SecRoute><WorkingHours/></SecRoute>}/>
            <Route path=":userID" element={<SecRoute><EditUser/></SecRoute>}/>
          </Route>
          <Route path="/system">
            <Route path="events" element={<SecRoute><Events/></SecRoute>}/>
            <Route path="status" element={<SecRoute><SystemStatus/></SecRoute>}/>
            <Route path="settings" element={<SecRoute><Settings/></SecRoute>}/>
            <Route path="calls" element={<SecRoute>
              <Calls
                callsEnabled={callsEnabled}
                toggleCallsStatus={togglesCallsStatus}/>
            </SecRoute>}/>
            <Route path="calls/:callID" element={<SecRoute><EditCall/></SecRoute>}/>
            <Route path="calls/add" element={<SecRoute><EditCall/></SecRoute>}/>
          </Route>
          <Route path="/alarms/:alarmID" element={<SecRoute><Alarm/></SecRoute>}/>
          <Route path="/reports" element={<SecRoute><Reports/></SecRoute>}/>
          <Route path="/login" element={<Login/>}/>
          <Route path='*' element={<NotFound/>}/>
        </Routes>
        {
          authCtx.isLoggedIn &&
          <Theme theme="g10">
            <Footer />
          </Theme>
        }
      </HashRouter>
    </div>
  );
}

export default App;
